"use client";

import {ReactNode, useEffect, useRef} from "react";
import {usePathname} from "@/i18n/navigation";
import {useAppDispatch, useAppStore} from "@/lib/hooks";
import {syncDialogDiagnostics, syncVisibleDialogs} from "@/lib/slices/dialogsSlice";
import {getOrchestrator} from "../singleton";
import {VisibleDialog} from "../types";
import {buildOrchestratorContext, detectPwaInstalled} from "./context";

/**
 * Bridges the (framework-agnostic) orchestrator singleton to React/Redux/router.
 *
 * Responsibilities — and nothing else:
 *  1. INPUT  — feed the engine fresh context from Redux + pathname.
 *  2. OUTPUT — mirror the engine's visible-dialog set into the `dialogs` slice.
 *  3. Notify the engine on store changes (debounced) and on navigation.
 *
 * Mount this once, high in the tree, inside <Provider> (Redux) and the router.
 */
export function DialogOrchestratorProvider({children}: {children: ReactNode}) {
    const store = useAppStore();
    const dispatch = useAppDispatch();
    const pathname = usePathname();

    const pathnameRef = useRef(pathname);
    const lastVisibleRef = useRef<string>("");
    pathnameRef.current = pathname;

    // 1 + 2: wire context provider and render sink exactly once.
    useEffect(() => {
        const orchestrator = getOrchestrator();
        const isPwaInstalled = detectPwaInstalled();
        let disposed = false;

        orchestrator.setContextProvider(() =>
            buildOrchestratorContext(store.getState(), pathnameRef.current, {
                isPwaInstalled,
                activeFlow: (orchestrator.flows.active()[0] as never) ?? null,
            }),
        );

        // AC-817: the sink is called synchronously from open()/close(), which
        // are reachable from inside a React commit (Radix `onOpenChange`) and
        // from a Redux notification. Dispatching from there re-enters the store
        // mid-reducer (Redux #3/#6), so the mirror update is deferred to a
        // microtask — the same shape the store subscription below already uses.
        // A burst still collapses to a single dispatch pair for the last state.
        let pendingVisible: VisibleDialog[] | null = null;
        let flushScheduled = false;

        const flushVisible = () => {
            flushScheduled = false;
            const visible = pendingVisible;
            pendingVisible = null;
            // Deliberately NOT gated on `disposed`: the store outlives this
            // provider, and the sink only runs on open/close — dropping a
            // queued flush would leave the mirror describing a dialog set the
            // engine no longer has, with nothing to correct it until the next
            // dialog event.
            if (!visible) return;
            // Guard against redundant dispatches → avoids a store-subscribe loop.
            const key = JSON.stringify(visible.map((v) => [v.instanceId, v.zIndex, v.focused]));
            if (key === lastVisibleRef.current) return;
            lastVisibleRef.current = key;
            // Strip `props` (may hold callbacks) so the store stays serialisable;
            // the host reads live props from the engine via `getProps`.
            const serialisable = visible.map((v) => ({
                id: v.id,
                instanceId: v.instanceId,
                zIndex: v.zIndex,
                blocking: v.blocking,
                focused: v.focused,
            }));
            dispatch(syncVisibleDialogs(serialisable));
            dispatch(
                syncDialogDiagnostics({
                    queueLength: orchestrator.queue.size(),
                    activeFlows: orchestrator.flows.active(),
                }),
            );
        };

        orchestrator.setRenderSink((visible: VisibleDialog[]) => {
            pendingVisible = visible;
            if (flushScheduled) return;
            flushScheduled = true;
            queueMicrotask(flushVisible);
        });

        // 3: re-evaluate when relevant store slices change (coalesced per tick).
        let scheduled = false;
        const unsubscribe = store.subscribe(() => {
            if (scheduled) return;
            scheduled = true;
            queueMicrotask(() => {
                scheduled = false;
                if (disposed) return;
                orchestrator.notifyContextChanged();
            });
        });

        return () => {
            disposed = true;
            unsubscribe();
        };
        // store/dispatch identities are stable for the app lifetime.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 3b: navigation — close non-persistent dialogs and re-evaluate.
    useEffect(() => {
        getOrchestrator().handleRouteChange();
    }, [pathname]);

    return <>{children}</>;
}
